import { computed, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useBaseStore } from '@/stores/base.store'
import { useI18n } from 'vue-i18n'
import { _translations } from './_translations'

export type Graph = Record<string, any>
export type MetaRecord = Record<string, any>
export type GraphLevel = 'project' | 'location' | 'hub'

export interface GraphListEntry {
	graph: Graph
	projectMeta: MetaRecord
	locationMeta: MetaRecord | null
	hubMeta: MetaRecord | null
	level: GraphLevel
}

const filterQuery = ref('')
const sortBy = ref<string>('project')
const reverseSort = ref(false)

const collator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true })

export function useGraphs() {
	const baseStore = useBaseStore()
	const { megalist } = storeToRefs(baseStore)
	const { t } = useI18n(_translations)

	const placeholder = '—'

	const sortOptions = computed(() => [
		{ label: 'graph', sortKey: 'name' },
		{ label: 'project', sortKey: 'project' },
		{ label: 'location', sortKey: 'location' },
		{ label: 'hub', sortKey: 'hub' },
		{ label: 'level', sortKey: 'level' },
	])

	const rawEntries = computed<GraphListEntry[]>(() => {
		const entries: GraphListEntry[] = []
		const projects = megalist.value?.projects ?? []

		const pushEntry = (
			graph: Graph | null | undefined,
			projectMeta: MetaRecord,
			level: GraphLevel,
			context: { locationMeta?: MetaRecord | null; hubMeta?: MetaRecord | null } = {},
		) => {
			if (!graph) return
			entries.push({
				graph,
				projectMeta,
				locationMeta: context.locationMeta ?? null,
				hubMeta: context.hubMeta ?? null,
				level,
			})
		}

		for (const project of projects) {
			if (!project || typeof project !== 'object') continue
			const projectMeta: MetaRecord = project.meta || {}

			if (Array.isArray(project.graphs)) {
				for (const graph of project.graphs) {
					pushEntry(graph, projectMeta, 'project')
				}
			}

			if (!Array.isArray(project.locations)) continue
			for (const location of project.locations) {
				if (!location || typeof location !== 'object') continue
				const locationMeta: MetaRecord = location.meta || {}

				if (Array.isArray(location.graphs)) {
					for (const graph of location.graphs) {
						pushEntry(graph, projectMeta, 'location', { locationMeta })
					}
				}

				if (!Array.isArray(location.hubs)) continue
				for (const hub of location.hubs) {
					if (!hub || typeof hub !== 'object') continue
					const hubMeta: MetaRecord = hub.meta || {}

					if (!Array.isArray(hub.graphs)) continue
					for (const graph of hub.graphs) {
						pushEntry(graph, projectMeta, 'hub', { locationMeta, hubMeta })
					}
				}
			}
		}

		return entries
	})

	const graphLabel = (graph: Graph) => graph?.name || graph?.serial || graph?._id || t('customGraph.noName')
	const projectLabel = (meta: MetaRecord | null) => {
		if (!meta) return placeholder
		const number = meta.projectNumber ? String(meta.projectNumber) : ''
		const name = meta.shortName || meta.name || meta._id || ''
		if (number && name) return `${number} ${name}`
		return number || name || placeholder
	}
	const projectTopLine = (meta: MetaRecord | null) => {
		if (!meta) return placeholder
		const number = meta.projectNumber ? String(meta.projectNumber) : ''
		const shortName = meta.shortName || ''
		const base = number || shortName || meta._id || placeholder
		if (number && shortName) return `${number} (${shortName})`
		if (number) return number
		if (shortName) return shortName
		return base
	}
	const projectFullName = (meta: MetaRecord | null) => {
		if (!meta) return placeholder
		return meta.name || meta.shortName || meta._id || placeholder
	}
	const locationLabel = (meta: MetaRecord | null) => meta?.name || meta?.shortName || meta?._id || placeholder
	const hubLabel = (meta: MetaRecord | null) => meta?.name || meta?.serial || meta?._id || placeholder
	const levelLabel = (level: GraphLevel) => {
		switch (level) {
			case 'project':
				return t('customGraph.list.project')
			case 'location':
				return t('customGraph.list.location')
			default:
				return t('customGraph.list.hub')
		}
	}

	const statusTitles = computed(() => ({
		project: t('customGraph.list.statusTooltip.project'),
		location: t('customGraph.list.statusTooltip.location'),
		hub: t('customGraph.list.statusTooltip.hub'),
	}))

	const filteredEntries = computed(() => {
		const query = filterQuery.value.trim().toLowerCase()
		if (!query) return rawEntries.value

		const metaStrings = (meta: MetaRecord | null | undefined) => {
			if (!meta || typeof meta !== 'object') return [] as string[]
			const keys = ['name', 'shortName', 'projectNumber', 'serial', '_id']
			return keys
				.map((key) => (meta as any)?.[key])
				.filter((value) => value !== undefined && value !== null && String(value).trim().length)
				.map((value) => String(value))
		}

		return rawEntries.value.filter((entry) => {
			const values = [
				graphLabel(entry.graph),
				...(entry.graph ? [entry.graph.serial, entry.graph._id].filter(Boolean) : []),
				projectLabel(entry.projectMeta),
				projectTopLine(entry.projectMeta),
				projectFullName(entry.projectMeta),
				...metaStrings(entry.projectMeta),
				locationLabel(entry.locationMeta),
				...metaStrings(entry.locationMeta),
				entry.level === 'hub' ? hubLabel(entry.hubMeta) : '',
				...metaStrings(entry.hubMeta),
				levelLabel(entry.level),
			]
			return values.some((value) => value && String(value).toLowerCase().includes(query))
		})
	})

	const graphEntries = computed<GraphListEntry[]>(() => {
		const entries = filteredEntries.value.slice()
		const levelOrder: Record<GraphLevel, number> = {
			project: 0,
			location: 1,
			hub: 2,
		}

		const compare = (a: GraphListEntry, b: GraphListEntry) => {
			const key = sortBy.value
			let result = 0

			switch (key) {
				case 'name':
					result = collator.compare(graphLabel(a.graph), graphLabel(b.graph))
					break
				case 'project':
					result = collator.compare(projectLabel(a.projectMeta), projectLabel(b.projectMeta))
					break
				case 'location':
					result = collator.compare(locationLabel(a.locationMeta), locationLabel(b.locationMeta))
					break
				case 'hub':
					result = collator.compare(
						a.level === 'hub' ? hubLabel(a.hubMeta) : placeholder,
						b.level === 'hub' ? hubLabel(b.hubMeta) : placeholder,
					)
					break
				case 'level':
					result = levelOrder[a.level] - levelOrder[b.level]
					break
				default:
					result = collator.compare(projectLabel(a.projectMeta), projectLabel(b.projectMeta))
					if (result === 0) {
						result = collator.compare(locationLabel(a.locationMeta), locationLabel(b.locationMeta))
					}
					if (result === 0) {
						result = collator.compare(graphLabel(a.graph), graphLabel(b.graph))
					}
			}
			return reverseSort.value ? -result : result
		}

		return entries.sort(compare)
	})

	const graphCount = computed(() => graphEntries.value.length)

	return {
		// state
		filterQuery,
		sortBy,
		reverseSort,

		// derived data
		sortOptions,
		graphEntries,
		graphCount,
		statusTitles,
		t,

		// helpers
		placeholder,
		graphLabel,
		projectLabel,
		projectTopLine,
		projectFullName,
		locationLabel,
		hubLabel,
		levelLabel,
	}
}
